'use client';

import { useState, useRef, useEffect } from 'react';

import Image from 'next/image';

import { useInView } from 'framer-motion';
import { Play, Pause, ArrowRight } from 'lucide-react';

import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useTranslation } from '@/lib/i18n/context';

interface Benefit {
  title: string;
  description: string;
  linkText: string;
  linkHref: string;
  imageUrl?: string;
  videoUrl?: string;
  imagePosition: 'left' | 'right';
  statCard?: {
    value: string;
    label: string;
    sublabel?: string;
  };
  testimonial?: {
    text: string;
    logoSrc: string;
    logoAlt: string;
  };
}

interface BenefitsSectionProps {
  title: string;
  subtitle?: string;
  learnMoreLink?: string;
  benefits: Benefit[];
  accentColor?: string;
  backgroundColor?: string;
  /** Color theme – defaults to "dark" for backwards compatibility */
  theme?: 'dark' | 'light';
}

const VideoPlayer = ({ videoUrl }: { videoUrl: string }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.5 });

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-play when in view (only on mobile)
  useEffect(() => {
    if (!isMobile) return;

    if (isInView && videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    } else if (!isInView && videoRef.current && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isInView, isPlaying, isMobile]);

  const handleMouseEnter = () => {
    if (!isMobile && videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div
      ref={containerRef}
      className="radius-18 group relative aspect-[4/3] w-full overflow-hidden rounded-[1.75rem] bg-neutral-100 ring-1 ring-neutral-900/5 [clip-path:inset(0_round_18px)] [contain:paint] [transform:translateZ(0)]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        loop
        muted
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        <source src={videoUrl} type="video/mp4" />
      </video>
      <button
        onClick={togglePlayPause}
        className="absolute bottom-4 ltr:right-4 rtl:left-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/85 text-neutral-900 backdrop-blur-md transition-all hover:scale-105 hover:bg-white"
        aria-label={
          isPlaying ? t.common['Pause video'] : t.common['Play video']
        }
      >
        {isPlaying ? (
          <Pause className="h-3.5 w-3.5" fill="currentColor" />
        ) : (
          <Play
            className="ltr:ml-0.5 rtl:mr-0.5 h-3.5 w-3.5"
            fill="currentColor"
          />
        )}
      </button>
    </div>
  );
};

export const BenefitsSection = ({
  title,
  subtitle,
  benefits,
  accentColor = 'emerald',
  backgroundColor,
  theme = 'dark',
}: BenefitsSectionProps) => {
  const { locale } = useTranslation();
  const isLight = theme === 'light';
  const resolvedBg = backgroundColor ?? (isLight ? '#ffffff' : '#050505');

  const sectionTextClass = isLight ? 'text-neutral-900' : 'text-white';
  const eyebrowClass = isLight ? 'text-neutral-500' : 'text-white/40';
  const titleColor = isLight ? 'text-neutral-900' : 'text-white';
  const subtitleClass = isLight ? 'text-neutral-500' : 'text-white/40';
  const benefitTitleClass = isLight ? 'text-neutral-900' : 'text-white';
  const benefitDescClass = isLight ? 'text-neutral-600' : 'text-white/50';
  const testimonialBorder = isLight ? 'border-neutral-200' : 'border-white/10';
  const testimonialLogoClass = isLight
    ? 'h-5 w-auto opacity-60 transition-all hover:opacity-90'
    : 'h-5 w-auto opacity-50 brightness-0 invert transition-all hover:opacity-80';
  const testimonialLinkClass = isLight
    ? 'group inline-flex items-center gap-2 text-sm text-neutral-500 transition-all hover:text-neutral-900'
    : 'group inline-flex items-center gap-2 text-sm text-white/40 transition-all hover:text-white';
  const cardBorder = isLight ? 'border-neutral-200/80' : 'border-white/[0.08]';
  const statLabelClass = isLight ? 'text-neutral-700' : 'text-white/60';
  const statSublabelClass = isLight ? 'text-neutral-500' : 'text-white/30';

  const getAccentClass = (type: 'text' | 'bg' | 'from' | 'to') => {
    if (isLight) {
      // In light mode we keep things tonal – the accent feels like ink rather than glow
      const light = {
        text: 'text-neutral-900 hover:text-neutral-700',
        bg: 'bg-neutral-900/60',
        from: 'from-neutral-100',
        to: 'to-neutral-50',
      };
      return light[type];
    }
    const colors = {
      emerald: {
        text: 'text-white/70 hover:text-white',
        bg: 'bg-white/60',
        from: 'from-white/5',
        to: 'to-white/[0.02]',
      },
      blue: {
        text: 'text-white/70 hover:text-white',
        bg: 'bg-white/60',
        from: 'from-white/5',
        to: 'to-white/[0.02]',
      },
      purple: {
        text: 'text-white/70 hover:text-white',
        bg: 'bg-white/60',
        from: 'from-white/5',
        to: 'to-white/[0.02]',
      },
    };
    return (
      colors[accentColor as keyof typeof colors]?.[type] || colors.emerald[type]
    );
  };

  return (
    <section
      className={`py-32 ${sectionTextClass}`}
      style={{ backgroundColor: resolvedBg }}
    >
      <div className="relative z-10 container">
        {/* Header */}
        <SubtleEntrance className="mb-20 text-center">
          <p
            className={`mb-4 text-xs font-medium tracking-widest uppercase ${eyebrowClass}`}
          >
            {locale === 'ar' ? 'ليش Open' : 'Why Open'}
          </p>
          <h2
            className={`font-serif text-2xl sm:text-3xl md:text-4xl ${titleColor}`}
          >
            {title}
          </h2>
          {subtitle && (
            <p className={`mx-auto mt-5 max-w-lg text-base ${subtitleClass}`}>
              {subtitle}
            </p>
          )}
        </SubtleEntrance>

        {/* Benefits */}
        <div className="space-y-24">
          {benefits.map((benefit, index) => (
            <SubtleEntrance
              key={index}
              className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20"
            >
              {/* Text Content */}
              <div
                className={`flex flex-col ${benefit.imagePosition === 'left' ? 'order-1 lg:order-2' : ''}`}
              >
                <div className="flex flex-1 flex-col justify-center space-y-5">
                  <h3
                    className={`max-w-md font-serif text-2xl md:text-3xl ${benefitTitleClass}`}
                  >
                    {benefit.title}
                  </h3>
                  <p
                    className={`max-w-md text-base leading-relaxed ${benefitDescClass}`}
                  >
                    {benefit.description}
                  </p>
                </div>
                {benefit.testimonial && (
                  <div className={`mt-10 border-t pt-8 ${testimonialBorder}`}>
                    <div className="mb-4">
                      <Image
                        src={benefit.testimonial.logoSrc}
                        alt={benefit.testimonial.logoAlt}
                        width={100}
                        height={28}
                        className={testimonialLogoClass}
                      />
                    </div>
                    <a href={benefit.linkHref} className={testimonialLinkClass}>
                      {benefit.testimonial.text}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                )}
              </div>

              {/* Image, Video, or Stat Card */}
              <div
                className={`${benefit.imagePosition === 'left' ? 'order-2 lg:order-1' : ''}`}
              >
                {benefit.videoUrl ? (
                  <VideoPlayer videoUrl={benefit.videoUrl} />
                ) : benefit.imageUrl ? (
                  <div
                    className={`relative aspect-[4/3] w-full overflow-hidden rounded-[1.75rem] border ${cardBorder}`}
                  >
                    <Image
                      src={benefit.imageUrl}
                      alt={benefit.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                ) : benefit.statCard ? (
                  <div
                    className={`relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-[1.75rem] border ${cardBorder} bg-gradient-to-br ${getAccentClass('from')} ${getAccentClass('to')}`}
                  >
                    <div className="p-8 text-center">
                      <div
                        className={`mb-4 font-sans font-normal tabular-nums text-6xl ${getAccentClass('text').split(' ')[0]}`}
                      >
                        {benefit.statCard.value}
                      </div>
                      <div className={`text-xl ${statLabelClass}`}>
                        {benefit.statCard.label}
                      </div>
                      {benefit.statCard.sublabel && (
                        <div className={`mt-2 text-sm ${statSublabelClass}`}>
                          {benefit.statCard.sublabel}
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </SubtleEntrance>
          ))}
        </div>
      </div>
    </section>
  );
};
